import {
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  styled,
  Box,
  Divider,
} from "@mui/material";
import { useTheme } from '@emotion/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { Logs, contactUs } from '../../services';
import { ContactUsFrame } from "../../assets";
import { contactUsSchema } from './validations';
import { ErrorText, Wrapper } from '../../components';
import { notification } from 'antd';

const CustomTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    background: "#fafafa",
  },
}));

export const ContactUs = () => {
  const theme = useTheme();
  const [showSuccess, setShowSuccess] = useState(false);

  const form = useForm({
    resolver: yupResolver(contactUsSchema),
    mode: 'all',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const { user } = useSelector((state) => state.auth);

  const companyId = user?.company?.id;
  const userEmail = user?.email;
  const userName = user?.username;
  const contactNumber = user?.contactNumber;

  const handleQuerySend = async (data) => {
    try {
      const res = await contactUs({
        message: data?.message,
        company: companyId,
        email: userEmail,
        name: userName,
        contactNumber,
      });
      if (res?.data) {
        setShowSuccess(true);
        notification.success({
          message: "Successfully Sent!",
          description: "Thank you for your enquiry.",
        });
        form.reset();
      }
    } catch (error) {
      Logs(error);
    }
  };

  return (
    <Wrapper
      sx={{
        overflow: { xs: "visible", md: "hidden" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: { xs: 2, md: 6 },
      }}
    >
      <form onSubmit={handleSubmit(handleQuerySend)} style={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 4, md: 6 },
            flexDirection: { xs: "column", md: "row" },
            mx: "auto",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: "1200px",
            width: "100%"
          }}
        >
          {/* Left Form Section */}
          <Paper
            elevation={3}
            sx={{
              flex: 1,
              borderRadius: "16px",
              overflow: "hidden",
              mt: { xs: 0, md: 2 }, // extra spacing for md+ screens
            }}
          >
            <Stack spacing={4} sx={{ py: 6, px: { xs: 3, md: 6 } }}>
              <Typography variant="h4" fontWeight={700}>
                We&apos;d love to hear from you!
              </Typography>

              <Divider />

              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontSize: "1.4rem" }}>
                  Write us a message
                </Typography>
                <CustomTextField
                  multiline
                  rows={5}
                  id="message"
                  placeholder="Write us a Message"
                  {...register('message')}
                  onInput={(e) => {
                    if (/^\s+$/.test(e.target.value)) {
                      e.target.value = ""; // clear if only spaces
                    }
                  }}
                />
                {!!errors.message && (
                  <ErrorText>{errors.message.message}</ErrorText>
                )}
              </Stack>

              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                }}
              >
                Please give as much info as you can. Our support team will get
                back to you shortly after you submit.
              </Typography>

              <Stack alignItems="flex-start">
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    px: 4,
                  }}
                  type="submit"
                >
                  Send Message
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Right Info Section */}
          <Stack
            sx={{
              flex: 1,
              textAlign: { xs: "center", md: "left" },
              alignItems: { xs: "center", md: "flex-start" },
              gap: 3,
            }}
          >
            <Box
              component="img"
              sx={{ width: { xs: "90%", md: "70%" }, borderRadius: "12px" }}
              src={ContactUsFrame}
            />
            <Typography variant="h3" fontWeight={700}>
              Let&apos;s Connect!
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}
            >
              We&apos;re here to help you with all your needs. Whether you have
              a question, feedback to share, or a credit request, we&apos;ve got
              you covered.
            </Typography>
          </Stack>
        </Box>
      </form>
    </Wrapper>
  );
};
